import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { EditableCreditCard, MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT } from '@platform-frontend/marketplace';
import { CreditCardService, CreditCardType, CreditCardValidators } from '@platform-frontend/payment-util';
import { formControlValue } from '@platform-frontend/ui-tools';
import { TypedChanges } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { CHECKOUT_TRANSLOCO_SCOPE } from '../../checkout-transloco-scope';
import { CreditCardValidationError } from '../model/credit-card-validation-error';

interface CreditCardForm {
  type: FormControl<'CREDIT_CARD'>;
  id: FormControl<number>;
  holderName: FormControl<string>;
  number: FormControl<string>;
  expiry: FormControl<string>;
  cvc: FormControl<string>;
}

@Component({
  selector: 'yat-credit-card-form',
  templateUrl: './credit-card-form.component.html',
  styleUrls: ['./credit-card-form.component.scss'],
  providers: [provideTranslocoScope(CHECKOUT_TRANSLOCO_SCOPE)]
})
export class CreditCardFormComponent implements OnChanges, OnInit, OnDestroy {
  constructor(private creditCardService: CreditCardService, private matomoTracker: MatomoTracker) {}

  @Input()
  input: Partial<EditableCreditCard> | null = null;

  @Input()
  validationError: CreditCardValidationError | null = null;

  @Input()
  buttonsClassName: string;

  @Output()
  save: EventEmitter<EditableCreditCard> = new EventEmitter();

  @Output()
  cancelMethod: EventEmitter<void> = new EventEmitter();

  focused: boolean = false;
  showMask: boolean = false;

  isNotEmpty = false;
  subscription: Subscription;

  readonly MATOMO_EVENT_CATEGORY_PAYMENT = MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT;

  creditCardForm = new FormGroup<CreditCardForm>({
    type: new FormControl('CREDIT_CARD', { nonNullable: true }),
    id: new FormControl(0, { nonNullable: true }),
    holderName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    number: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        CreditCardValidators.numberFormat(this.creditCardService),
        CreditCardValidators.cardType(this.creditCardService, 'MasterCard', 'Visa', 'AmericanExpress')
      ]
    }),
    expiry: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, CreditCardValidators.expiryFormat(this.creditCardService), CreditCardValidators.expiryDate(this.creditCardService)]
    }),
    cvc: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, CreditCardValidators.cvc]
    })
  });

  number = this.creditCardForm.controls.number;
  expiry = this.creditCardForm.controls.expiry;

  cardType$: Observable<CreditCardType | 'INVALID' | null>;
  private useInvalidCardType$ = new BehaviorSubject<boolean>(false);

  ngOnChanges(changes: TypedChanges<CreditCardFormComponent>): void {
    if (changes.input) {
      this.creditCardForm.reset(this.input ?? { type: 'CREDIT_CARD', id: 0 });
    }

    if (changes.validationError) {
      this.updateValidationError();
    }
  }

  private updateValidationError(): void {
    if (this.validationError === 'CVC_DECLINED') {
      this.creditCardForm.controls.cvc.setErrors({ cvcDeclined: true });
    } else if (this.validationError === 'CARD_EXPIRED') {
      this.creditCardForm.controls.expiry.setErrors({ cardExpired: true });
    } else if (this.validationError === 'EXPIRY_DATE_INVALID') {
      this.creditCardForm.controls.expiry.setErrors({ expiryDateInvalid: true });
    } else if (this.validationError === 'INVALID_CARD_NUMBER') {
      this.creditCardForm.controls.number.setErrors({ invalidCardNumber: true });
    }
  }

  ngOnInit(): void {
    this.cardType$ = combineLatest([
      formControlValue<string | null>(this.number).pipe(map(number => this.creditCardService.getCardType(number, { removeWhitespace: true }))),
      this.useInvalidCardType$
    ]).pipe(map(([cardType, useInvalidCardType]) => (useInvalidCardType && this.number.invalid ? 'INVALID' : cardType)));
    this.subscription = this.creditCardForm.valueChanges.subscribe(value => {
      this.isNotEmpty = !!value.number;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  activateInvalidCardType(): void {
    this.useInvalidCardType$.next(true);
  }

  submitForm(value: EditableCreditCard): void {
    this.save.next(value);
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_PAYMENT, 'Confirm_PaymentMethodForm', 'Creditcard');
  }

  cancel(): void {
    this.cancelMethod.emit();
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_PAYMENT, 'Cancel_PaymentMethodForm', 'Creditcard');
  }
}
